import React, { useState, useEffect, useMemo } from 'react';
import { Link } from 'react-router-dom';
import { Badge, Button, Card, Col, Row, Table, Form, Nav } from 'react-bootstrap';
import * as XLSX from 'xlsx';
import Header from '../layouts/Header';
import Footer from '../layouts/Footer';
import { useLoading } from '../contexts/LoadingContext';
import axiosInstance from '../services/axiosInterceptor';
import { getToken } from '../services/authorize';
import { formatDateTH } from '../utils/dateUtils';
import { useAuth } from '../services/authProvider';
import { getTrackingRoleLabel, getLevelSuffix } from '../utils/roleLabels';

const API = process.env.REACT_APP_API;

const UPLOAD_BASE = (process.env.REACT_APP_API || 'http://localhost:3002/api').replace(/\/api\/?$/, '').replace(/\/$/, '');

// เกณฑ์ตั้งต้น: นักเรียนที่ขาดเรียนในวิชานั้นตั้งแต่ 3 ครั้งขึ้นไป
const DEFAULT_MIN_ABSENT = 3;

const StudentAvatar = ({ photo, name, size = 28 }) => {
  const initial = name ? name.trim().charAt(0) : '?';
  const src = photo ? `${UPLOAD_BASE}${photo.startsWith('/') ? photo : `/${photo}`}` : null;
  const circleStyle = { width: size, height: size, borderRadius: '50%', flexShrink: 0 };
  return src
    ? <img src={src} alt="" style={{ ...circleStyle, objectFit: 'cover' }} />
    : <span style={{ ...circleStyle, backgroundColor: '#adb5bd', color: '#fff', display: 'inline-flex', alignItems: 'center', justifyContent: 'center', fontSize: size * 0.42, fontWeight: 600 }}>{initial}</span>;
};

const subjectTitle = (r) => `${r.sub_subid || ''} ${r.sub_subname || ''}`.trim() || '-';

const num = (v) => Number(v) || 0;

// การ์ดนักเรียน 1 คนในวิชาหนึ่ง สำหรับมุมมองการ์ด
const SubjectStudentCard = ({ r }) => (
  <div className="border rounded p-3">
    <div className="d-flex align-items-center gap-2">
      <StudentAvatar photo={r.st_photo} name={r.st_name} />
      <div className="flex-grow-1" style={{ minWidth: 0 }}>
        <div className="fw-semibold text-truncate">{r.st_name}</div>
        <div className="fs-xs text-muted">{r.st_idnumber} · ม.{r.c_id}/{r.r_no}</div>
      </div>
    </div>
    <div className="fs-xs text-muted mt-2">{subjectTitle(r)} · {r.teachers || 'ไม่ระบุครูผู้สอน'}</div>
    <div className="d-flex flex-wrap gap-3 mt-2 pt-2 border-top fs-sm">
      <span className="text-danger fw-semibold">ขาด {num(r.absent_count)}</span>
      <span>สาย {num(r.late_count)}</span>
      <span>ลา {num(r.leave_count)}</span>
      <span>มา {num(r.present_count)}</span>
      <span className="text-muted">รวม {num(r.total)}</span>
    </div>
    {r.last_absent_date && (
      <div className="fs-xs text-muted mt-1">ขาดครั้งล่าสุด {formatDateTH(r.last_absent_date)}</div>
    )}
  </div>
);

export default function SubjectAttendanceTracking() {
  const currentSkin = localStorage.getItem('skin-mode') ? 'dark' : '';
  const [skin, setSkin] = useState(currentSkin);
  const { setLoading } = useLoading();
  const { posIds } = useAuth();
  const roleLabel = getTrackingRoleLabel(posIds);
  const levelSuffix = getLevelSuffix(posIds);

  const switchSkin = (s) => {
    const tables = document.getElementsByTagName('table');
    for (const t of tables) {
      const theads = t.querySelectorAll('thead');
      if (s === 'dark') theads.forEach((th) => th.classList.add('table-dark'));
      else theads.forEach((th) => th.classList.remove('table-dark'));
    }
  };
  useEffect(() => { switchSkin(skin); });

  const [yearId, setYearId] = useState(null);
  const [classList, setClassList] = useState([]);
  const [roomList, setRoomList] = useState([]);

  // ---- ตัวกรองที่ส่งไป backend ----
  const [classId, setClassId] = useState('');
  const [roomId, setRoomId] = useState('');
  const [semester, setSemester] = useState('');
  const [minAbsent, setMinAbsent] = useState(DEFAULT_MIN_ABSENT);
  const [startDate, setStartDate] = useState('');
  const [endDate, setEndDate] = useState('');

  // ---- ตัวกรองฝั่งหน้าเว็บ ----
  const [subjectKey, setSubjectKey] = useState('');
  const [keyword, setKeyword] = useState('');

  const [rows, setRows] = useState([]);
  const [loaded, setLoaded] = useState(false);
  const [appliedMinAbsent, setAppliedMinAbsent] = useState(DEFAULT_MIN_ABSENT);
  const [activeTab, setActiveTab] = useState('bySubject');
  const [expanded, setExpanded] = useState(() => new Set());
  const [listView, setListView] = useState(() =>
    (typeof window !== 'undefined' && window.innerWidth < 768) ? 'card' : 'table');

  // ปีการศึกษาล่าสุด
  useEffect(() => {
    axiosInstance
      .get(`${API}/getLastestStudyYear`, { headers: { authorization: `Bearer ${getToken()}` } })
      .then((res) => { if (res.data?.year_id) setYearId(res.data.year_id); })
      .catch(() => {});
  }, []);

  // ชั้น/ห้องที่ผู้ใช้คนนี้มีสิทธิ์เห็น — server คืนตาม scope เดียวกับตารางข้อมูล
  // (ครูประจำชั้นได้เฉพาะห้องที่ตนเป็นที่ปรึกษา, หัวหน้าระดับได้เฉพาะระดับของตน)
  useEffect(() => {
    if (!yearId) return;
    axiosInstance
      .get(`${API}/tracking/filterOptions/${yearId}`, { headers: { authorization: `Bearer ${getToken()}` } })
      .then((res) => {
        setClassList(res.data?.classes || []);
        setRoomList(res.data?.rooms || []);
      })
      .catch(() => {});
  }, [yearId]);

  // ล้างชั้นที่เลือกไว้ถ้าไม่อยู่ในสิทธิ์ของผู้ใช้ เพื่อไม่ให้ตารางว่างโดยไม่รู้สาเหตุ
  useEffect(() => {
    if (classList.length && classId && !classList.some((c) => String(c.c_id) === String(classId))) setClassId('');
  }, [classList, classId]);

  // ห้องที่เลือกได้ตามชั้นที่เลือกอยู่ (ถ้าไม่เลือกชั้น = ทุกห้องที่มีสิทธิ์เห็น)
  const roomOptions = useMemo(
    () => (classId ? roomList.filter((r) => String(r.c_id) === String(classId)) : roomList),
    [roomList, classId]
  );
  // ล้างห้องที่เลือกเมื่อไม่อยู่ในรายการที่เลือกได้แล้ว (เปลี่ยนชั้น หรือไม่มีสิทธิ์เห็น)
  useEffect(() => {
    if (roomOptions.length && roomId && !roomOptions.some((r) => String(r.r_id) === String(roomId))) setRoomId('');
  }, [roomOptions, roomId]);

  const fetchRows = () => {
    if (!yearId) return;
    const min = parseInt(minAbsent, 10);
    const safeMin = Number.isInteger(min) && min > 0 ? min : DEFAULT_MIN_ABSENT;
    setLoading(true);
    setLoaded(false);
    const params = new URLSearchParams();
    params.append('minAbsent', safeMin);
    if (classId) params.append('classId', classId);
    if (roomId) params.append('roomId', roomId);
    if (semester) params.append('semester', semester);
    if (startDate && endDate) { params.append('startDate', startDate); params.append('endDate', endDate); }
    axiosInstance
      .get(`${API}/getAtRiskStudentsBySubject/${yearId}?${params}`, {
        headers: { authorization: `Bearer ${getToken()}` },
      })
      .then((res) => {
        setRows(res.data || []);
        setAppliedMinAbsent(safeMin);
        setSubjectKey('');
        setExpanded(new Set());
      })
      .catch(() => setRows([]))
      .finally(() => { setLoading(false); setLoaded(true); });
  };

  useEffect(() => {
    if (yearId && !loaded) fetchRows();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [yearId]);

  // รายการวิชาสำหรับ dropdown (เฉพาะวิชาที่มีนักเรียนเข้าเกณฑ์)
  const subjectOptions = useMemo(() => {
    const map = new Map();
    rows.forEach((r) => {
      const key = String(r.sub_id);
      if (!map.has(key)) map.set(key, { key, label: subjectTitle(r), count: 0 });
      map.get(key).count += 1;
    });
    return Array.from(map.values()).sort((a, b) => b.count - a.count || a.label.localeCompare(b.label, 'th'));
  }, [rows]);

  const filteredRows = useMemo(() => {
    const kw = keyword.trim().toLowerCase();
    return rows.filter((r) => {
      if (subjectKey && String(r.sub_id) !== subjectKey) return false;
      if (!kw) return true;
      return [r.st_name, r.st_idnumber, r.sub_subid, r.sub_subname, r.teachers]
        .some((v) => String(v || '').toLowerCase().includes(kw));
    });
  }, [rows, subjectKey, keyword]);

  // จัดกลุ่มตามวิชา — เรียงวิชาที่มีนักเรียนเข้าเกณฑ์มากที่สุดก่อน
  const groups = useMemo(() => {
    const map = new Map();
    filteredRows.forEach((r) => {
      const key = String(r.sub_id);
      if (!map.has(key)) {
        map.set(key, {
          key,
          sub_subid: r.sub_subid,
          sub_subname: r.sub_subname,
          sub_semester: r.sub_semester,
          teachers: new Set(),
          students: [],
          absentTotal: 0,
        });
      }
      const g = map.get(key);
      if (r.teachers) String(r.teachers).split(',').forEach((t) => g.teachers.add(t.trim()));
      g.students.push(r);
      g.absentTotal += num(r.absent_count);
    });
    return Array.from(map.values())
      .map((g) => ({ ...g, teachers: Array.from(g.teachers).filter(Boolean).join(', ') }))
      .sort((a, b) => b.students.length - a.students.length || b.absentTotal - a.absentTotal);
  }, [filteredRows]);

  const stats = useMemo(() => ({
    subjects: groups.length,
    students: new Set(filteredRows.map((r) => r.st_id)).size,
    entries: filteredRows.length,
    absent: filteredRows.reduce((sum, r) => sum + num(r.absent_count), 0),
  }), [groups, filteredRows]);

  const toggleGroup = (key) => {
    setExpanded((prev) => {
      const next = new Set(prev);
      if (next.has(key)) next.delete(key); else next.add(key);
      return next;
    });
  };

  const allExpanded = groups.length > 0 && groups.every((g) => expanded.has(g.key));
  const toggleAll = () => setExpanded(allExpanded ? new Set() : new Set(groups.map((g) => g.key)));

  const handleExportExcel = () => {
    const wb = XLSX.utils.book_new();
    const rowsOut = [
      ['ติดตามขาดเรียนรายวิชา'],
      [`เกณฑ์: ขาดเรียนตั้งแต่ ${appliedMinAbsent} ครั้งขึ้นไป`],
      [`ช่วงวันที่: ${startDate && endDate ? `${startDate} ถึง ${endDate}` : 'ทั้งปีการศึกษา'}`],
      [`สรุป: ${stats.subjects} วิชา / ${stats.students} คน / ${stats.entries} รายการ`],
      [],
      ['#', 'วิชา', 'ภาคเรียน', 'ครูผู้สอน', 'เลขประจำตัว', 'ชื่อ-สกุล', 'ชั้น/ห้อง', 'ขาด', 'สาย', 'ลา', 'มา', 'รวม', 'ขาดครั้งล่าสุด'],
    ];
    groups.forEach((g) => {
      g.students.forEach((r, idx) => {
        rowsOut.push([
          idx + 1,
          subjectTitle(r),
          r.sub_semester || '-',
          r.teachers || '-',
          r.st_idnumber,
          r.st_name,
          `ม.${r.c_id}/${r.r_no}`,
          num(r.absent_count),
          num(r.late_count),
          num(r.leave_count),
          num(r.present_count),
          num(r.total),
          r.last_absent_date ? formatDateTH(r.last_absent_date) : '-',
        ]);
      });
    });
    const ws = XLSX.utils.aoa_to_sheet(rowsOut);
    ws['!cols'] = [{ wch: 5 }, { wch: 32 }, { wch: 9 }, { wch: 24 }, { wch: 12 }, { wch: 26 }, { wch: 10 },
      { wch: 7 }, { wch: 7 }, { wch: 7 }, { wch: 7 }, { wch: 7 }, { wch: 14 }];
    XLSX.utils.book_append_sheet(wb, ws, 'ติดตามรายวิชา');
    XLSX.writeFile(wb, `ติดตามขาดเรียนรายวิชา_ขาด${appliedMinAbsent}ครั้งขึ้นไป.xlsx`);
  };

  const emptyState = (
    <div className="text-center py-5 text-muted">
      <i className="ri-emotion-happy-line fs-3 d-block mb-2"></i>
      ไม่พบนักเรียนที่ขาดเรียนถึงเกณฑ์ {appliedMinAbsent} ครั้งในรายวิชาใด
    </div>
  );

  return (
    <React.Fragment>
      <Header onSkin={setSkin} />
      <div className="main main-app p-3 p-lg-4">
        <div className="d-sm-flex align-items-center justify-content-between mb-4">
          <div>
            <ol className="breadcrumb fs-sm mb-1">
              <li className="breadcrumb-item"><Link to="#">{roleLabel}</Link></li>
              <li className="breadcrumb-item active" aria-current="page">ติดตามขาดเรียนรายวิชา</li>
            </ol>
            <h4 className="main-title mb-0">ติดตามขาดเรียนรายวิชา{levelSuffix}</h4>
          </div>
          {filteredRows.length > 0 && (
            <Button variant="outline-success" size="sm" onClick={handleExportExcel}>
              <i className="ri-file-excel-2-line me-1"></i>Excel
            </Button>
          )}
        </div>

        <Card className="card-one mb-3">
          <Card.Body>
            <Row className="g-2 align-items-end">
              <Col md={2}>
                <Form.Label className="fs-sm mb-1">ระดับชั้น</Form.Label>
                <Form.Select value={classId} onChange={(e) => setClassId(e.target.value)}>
                  <option value="">ทุกชั้น</option>
                  {classList.map((c) => (
                    <option key={c.c_id} value={c.c_id}>ม.{c.c_class || c.c_id}</option>
                  ))}
                </Form.Select>
              </Col>
              <Col md={2}>
                <Form.Label className="fs-sm mb-1">ห้องเรียน</Form.Label>
                <Form.Select value={roomId} onChange={(e) => setRoomId(e.target.value)}>
                  <option value="">ทุกห้อง</option>
                  {roomOptions.map((r) => (
                    <option key={r.r_id} value={r.r_id}>ม.{r.c_class || r.c_id}/{r.r_no}</option>
                  ))}
                </Form.Select>
              </Col>
              <Col md={2}>
                <Form.Label className="fs-sm mb-1">ภาคเรียน</Form.Label>
                <Form.Select value={semester} onChange={(e) => setSemester(e.target.value)}>
                  <option value="">ทุกภาคเรียน</option>
                  <option value="1">ภาคเรียนที่ 1</option>
                  <option value="2">ภาคเรียนที่ 2</option>
                </Form.Select>
              </Col>
              <Col md={2}>
                <Form.Label className="fs-sm mb-1">ขาดอย่างน้อย (ครั้ง)</Form.Label>
                <Form.Control
                  type="number"
                  min={1}
                  value={minAbsent}
                  onChange={(e) => setMinAbsent(e.target.value)}
                  onKeyDown={(e) => { if (e.key === 'Enter') fetchRows(); }}
                />
              </Col>
              <Col md={2}>
                <Form.Label className="fs-sm mb-1">ตั้งแต่วันที่</Form.Label>
                <Form.Control type="date" value={startDate} onChange={(e) => setStartDate(e.target.value)} />
              </Col>
              <Col md={2}>
                <Form.Label className="fs-sm mb-1">ถึงวันที่</Form.Label>
                <Form.Control type="date" value={endDate} onChange={(e) => setEndDate(e.target.value)} />
              </Col>
            </Row>
            <Row className="g-2 mt-1 align-items-end">
              <Col md={4}>
                <Form.Label className="fs-sm mb-1">วิชา</Form.Label>
                <Form.Select value={subjectKey} onChange={(e) => setSubjectKey(e.target.value)}>
                  <option value="">ทุกวิชา ({subjectOptions.length} วิชา)</option>
                  {subjectOptions.map((s) => (
                    <option key={s.key} value={s.key}>{s.label} ({s.count} คน)</option>
                  ))}
                </Form.Select>
              </Col>
              <Col md={4}>
                <Form.Label className="fs-sm mb-1">ค้นหา</Form.Label>
                <Form.Control
                  type="text"
                  value={keyword}
                  onChange={(e) => setKeyword(e.target.value)}
                  placeholder="ชื่อนักเรียน / เลขประจำตัว / รหัสวิชา / ครูผู้สอน"
                />
              </Col>
              <Col md={2}>
                <Button variant="primary" className="w-100" onClick={fetchRows} disabled={!yearId}>
                  <i className="ri-filter-3-line me-1"></i>กรอง
                </Button>
              </Col>
            </Row>
            <div className="fs-xs text-muted mt-2">
              แสดงนักเรียนที่ขาดเรียนตั้งแต่ <strong>{appliedMinAbsent}</strong> ครั้งขึ้นไปในรายวิชานั้น
              {startDate && endDate ? ` ระหว่าง ${formatDateTH(startDate)} - ${formatDateTH(endDate)}` : ' ตลอดปีการศึกษา'}
              {' '}(ต้องเลือกทั้งวันเริ่มและวันสิ้นสุดจึงจะกรองตามช่วงวันที่)
            </div>
          </Card.Body>
        </Card>

        <Row className="g-2 mb-3">
          {[
            { label: 'วิชาที่พบ', value: stats.subjects, variant: 'primary' },
            { label: 'นักเรียน (ไม่ซ้ำคน)', value: stats.students, variant: 'warning' },
            { label: 'รายการ (คน × วิชา)', value: stats.entries, variant: 'info' },
            { label: 'รวมจำนวนครั้งที่ขาด', value: stats.absent, variant: 'danger' },
          ].map(({ label, value, variant }) => (
            <Col xs={6} md={3} key={label}>
              <Card className="card-one">
                <Card.Body className="text-center p-3">
                  <h3 className={`mb-0 text-${variant}`}>{value}</h3>
                  <small className="text-muted">{label}</small>
                </Card.Body>
              </Card>
            </Col>
          ))}
        </Row>

        <Nav variant="tabs" className="mb-3" activeKey={activeTab} onSelect={(k) => setActiveTab(k)}>
          <Nav.Item><Nav.Link eventKey="bySubject"><i className="ri-book-2-line me-1"></i>แยกตามวิชา</Nav.Link></Nav.Item>
          <Nav.Item><Nav.Link eventKey="flat"><i className="ri-list-check me-1"></i>รายการทั้งหมด</Nav.Link></Nav.Item>
        </Nav>

        {activeTab === 'bySubject' && (
          <Card className="card-one mb-4">
            <Card.Header className="d-flex flex-wrap align-items-center gap-2">
              <Card.Title as="h6" className="mb-0">
                รายวิชาที่มีนักเรียนขาดเรียนถึงเกณฑ์
                <Badge bg="secondary" className="ms-2">{groups.length} วิชา</Badge>
              </Card.Title>
              {groups.length > 0 && (
                <Button variant="outline-secondary" size="sm" className="ms-auto" onClick={toggleAll}>
                  <i className={`ri-arrow-${allExpanded ? 'up' : 'down'}-s-line me-1`}></i>
                  {allExpanded ? 'ย่อทั้งหมด' : 'ขยายทั้งหมด'}
                </Button>
              )}
            </Card.Header>
            <Card.Body className="p-0">
              {loaded && groups.length === 0 ? emptyState : (
                <Table hover responsive className="mb-0 align-middle">
                  <thead className="table-secondary">
                    <tr>
                      <th className="text-center" style={{ width: 50 }}>#</th>
                      <th>วิชา</th>
                      <th>ครูผู้สอน</th>
                      <th className="text-center" style={{ width: 90 }}>ภาคเรียน</th>
                      <th className="text-center" style={{ width: 110 }}>นักเรียน</th>
                      <th className="text-center text-danger" style={{ width: 110 }}>รวมขาด</th>
                      <th className="text-center" style={{ width: 60 }}></th>
                    </tr>
                  </thead>
                  <tbody>
                    {groups.map((g, idx) => {
                      const open = expanded.has(g.key);
                      return (
                        <React.Fragment key={g.key}>
                          <tr style={{ cursor: 'pointer' }} onClick={() => toggleGroup(g.key)}>
                            <td className="text-center text-muted">{idx + 1}</td>
                            <td className="fw-semibold">{`${g.sub_subid || ''} ${g.sub_subname || ''}`.trim() || '-'}</td>
                            <td>{g.teachers || <span className="text-muted">-</span>}</td>
                            <td className="text-center">{g.sub_semester || '-'}</td>
                            <td className="text-center"><Badge bg="warning">{g.students.length} คน</Badge></td>
                            <td className="text-center"><span className="text-danger fw-semibold">{g.absentTotal}</span></td>
                            <td className="text-center">
                              <i className={`ri-arrow-${open ? 'up' : 'down'}-s-line`}></i>
                            </td>
                          </tr>
                          {open && (
                            <tr>
                              <td colSpan={7} className="p-0">
                                <Table size="sm" className="mb-0 align-middle">
                                  <thead>
                                    <tr className="fs-xs text-muted">
                                      <th style={{ width: 50 }}></th>
                                      <th style={{ width: 110 }}>เลขประจำตัว</th>
                                      <th>ชื่อ-สกุล</th>
                                      <th className="text-center" style={{ width: 90 }}>ชั้น/ห้อง</th>
                                      <th className="text-center" style={{ width: 70 }}>ขาด</th>
                                      <th className="text-center" style={{ width: 70 }}>สาย</th>
                                      <th className="text-center" style={{ width: 70 }}>ลา</th>
                                      <th className="text-center" style={{ width: 70 }}>มา</th>
                                      <th className="text-center" style={{ width: 70 }}>รวม</th>
                                      <th className="text-center" style={{ width: 130 }}>ขาดครั้งล่าสุด</th>
                                    </tr>
                                  </thead>
                                  <tbody>
                                    {g.students.map((r) => (
                                      <tr key={`${r.st_id}-${r.sub_id}`}>
                                        <td></td>
                                        <td>{r.st_idnumber}</td>
                                        <td>
                                          <div className="d-flex align-items-center gap-2">
                                            <StudentAvatar photo={r.st_photo} name={r.st_name} size={24} />
                                            <span>{r.st_name}</span>
                                          </div>
                                        </td>
                                        <td className="text-center">ม.{r.c_id}/{r.r_no}</td>
                                        <td className="text-center"><span className="text-danger fw-semibold">{num(r.absent_count)}</span></td>
                                        <td className="text-center">{num(r.late_count)}</td>
                                        <td className="text-center">{num(r.leave_count)}</td>
                                        <td className="text-center">{num(r.present_count)}</td>
                                        <td className="text-center text-muted">{num(r.total)}</td>
                                        <td className="text-center">{r.last_absent_date ? formatDateTH(r.last_absent_date) : '-'}</td>
                                      </tr>
                                    ))}
                                  </tbody>
                                </Table>
                              </td>
                            </tr>
                          )}
                        </React.Fragment>
                      );
                    })}
                  </tbody>
                </Table>
              )}
            </Card.Body>
          </Card>
        )}

        {activeTab === 'flat' && (
          <Card className="card-one mb-4">
            <Card.Header className="d-flex flex-wrap align-items-center gap-2">
              <Card.Title as="h6" className="mb-0">
                รายการทั้งหมด
                <Badge bg="secondary" className="ms-2">{filteredRows.length} รายการ</Badge>
              </Card.Title>
              <div className="btn-group btn-group-sm ms-auto" role="group" aria-label="สลับมุมมอง">
                <button type="button" className={`btn btn${listView === 'table' ? '' : '-outline'}-primary`}
                  onClick={() => setListView('table')} title="มุมมองตาราง">
                  <i className="ri-table-line" />
                </button>
                <button type="button" className={`btn btn${listView === 'card' ? '' : '-outline'}-primary`}
                  onClick={() => setListView('card')} title="มุมมองการ์ด">
                  <i className="ri-layout-grid-line" />
                </button>
              </div>
            </Card.Header>
            <Card.Body className="p-0">
              {loaded && filteredRows.length === 0 ? emptyState : listView === 'card' ? (
                <div className="p-3">
                  <Row className="g-3">
                    {filteredRows.map((r) => (
                      <Col key={`${r.st_id}-${r.sub_id}`} xs={12} md={6} xl={4}>
                        <SubjectStudentCard r={r} />
                      </Col>
                    ))}
                  </Row>
                </div>
              ) : (
                <Table hover responsive className="mb-0 align-middle">
                  <thead className="table-secondary">
                    <tr>
                      <th className="text-center" style={{ width: 50 }}>#</th>
                      <th>วิชา</th>
                      <th>ครูผู้สอน</th>
                      <th style={{ width: 110 }}>เลขประจำตัว</th>
                      <th>ชื่อ-สกุล</th>
                      <th className="text-center" style={{ width: 90 }}>ชั้น/ห้อง</th>
                      <th className="text-center text-danger" style={{ width: 70 }}>ขาด</th>
                      <th className="text-center" style={{ width: 70 }}>สาย</th>
                      <th className="text-center" style={{ width: 70 }}>ลา</th>
                      <th className="text-center" style={{ width: 70 }}>มา</th>
                      <th className="text-center" style={{ width: 70 }}>รวม</th>
                      <th className="text-center" style={{ width: 130 }}>ขาดครั้งล่าสุด</th>
                    </tr>
                  </thead>
                  <tbody>
                    {filteredRows.map((r, idx) => (
                      <tr key={`${r.st_id}-${r.sub_id}`}>
                        <td className="text-center text-muted">{idx + 1}</td>
                        <td>{subjectTitle(r)}</td>
                        <td>{r.teachers || <span className="text-muted">-</span>}</td>
                        <td>{r.st_idnumber}</td>
                        <td>
                          <div className="d-flex align-items-center gap-2">
                            <StudentAvatar photo={r.st_photo} name={r.st_name} />
                            <span>{r.st_name}</span>
                          </div>
                        </td>
                        <td className="text-center">ม.{r.c_id}/{r.r_no}</td>
                        <td className="text-center"><span className="text-danger fw-semibold">{num(r.absent_count)}</span></td>
                        <td className="text-center">{num(r.late_count)}</td>
                        <td className="text-center">{num(r.leave_count)}</td>
                        <td className="text-center">{num(r.present_count)}</td>
                        <td className="text-center text-muted">{num(r.total)}</td>
                        <td className="text-center">{r.last_absent_date ? formatDateTH(r.last_absent_date) : '-'}</td>
                      </tr>
                    ))}
                  </tbody>
                </Table>
              )}
            </Card.Body>
          </Card>
        )}

        <Footer />
      </div>
    </React.Fragment>
  );
}
